import { ACTION_FREQUENCY_TYPE, EDGE_TYPE, FREQUENCY_TYPE, Linqset, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, getHoursPerItem, getHoursPerYear, getTimesPerYear, resetNodeForModel, roundNodeInsights, getInsights, isRoleSystem, getInsightsWithoutCreate, resetNodeCostsForModel, resetCostsAndTotalHours, getCarbonEmissions, NO_ROLE, countPeople, ORGANISATION_MODEL_KEY, NodeInsights, allocateEmissions, getCost, addAllocations, addToIndexValue, addIndexedValues, createIndexedValues, setIndexValue, getIndexValue, addEmissions, createCostAllocation, sumAllocations, isModelNode, getSuperTypeName, isSupplyChain, convertModelledCapacityToTotal, isSystemInfrastructure, isRolePeople, initialiseIndexedValues, getInsightsForNode, LINQSET_TYPE, resetAllModelInsights, GraphData, WORK_TYPE, joinedInformationKeys } from "./linqset-model"
import { ACTION_TYPE, InsightTotal, InsightsFilter, ModelDetails, ModelInsights, ModelValueContributions, addValueContributions, getValueFromModelsInSet, NodeGroupModelInsights, Organisation, createActionType, createInsightTotal, createModelInsights, createNodeGroupModelInsights, addAssetInsights, getOrganisationModelList, getTotalModelCost, roundModelInsights, defaultCapacityHours } from "./organisation"
import { deriveAssetValues, deriveInformationValues, isFrictionOutput, setAssetValues, setInformationReach } from "./information-value"
import { isWait, setExceptionWork } from "./exception-work"
import { contributeValue, valueAtRisk } from "./value-contribution"

class ProcessingEdge {
    constructor(public source?:LinqsetNode, public edge?:LinqsetEdge, public target?:LinqsetNode) {}
}

export const processLinqset = async (linqset:Linqset, organisationLinqset:Linqset, ignoreFilter:boolean, organisation?:Organisation) => {
    if (!linqset.model) return
    
    const models = organisation?getOrganisationModelList(organisation, organisationLinqset, linqset.project):[linqset.model]

    if (linqset.isOrganisationModel()) {
        calculateCostsAndEmissionsForOrganisation(organisationLinqset, models, organisation, true)
    }else {   
        const startingNodes = getStartingNodes(linqset.nodes, linqset.edges)

        // first set the number processed and hours on all action and info nodes
        setTimeForActionsAndInfo(linqset, startingNodes, ignoreFilter)
        // now set the number processed and time for roles and systems. returns a map of actions indexed by system/role node key
        const actionsMap = setTimeForPeopleAndSystems(linqset, ignoreFilter)

        // set the costs on role and system nodes based on hours and cost/ These get proportioned to node model insights
        calculateCostsAndEmissionsForOrganisation(organisationLinqset, models, organisation, false)

        // allocate the costs from the role and system nodes to the actions
        setCostsOnActions(linqset, actionsMap, ignoreFilter)

        // how each piece of information is used, which values are derived from below and friction is judged by
        setInformationReach([linqset])
        // set total costs and hours and costs on info nodes. Also set values and update model insights. The joins into
        // other models are the organisation's, as this model's own graph only has those into it
        processModelCostsAndValue(linqset, startingNodes, ignoreFilter, joinedInformationKeys([...(organisationLinqset?.edges||[]), ...linqset.edges]))
        // create model insights
        linqset.insights = createModelInsightsFromNodes(linqset, linqset.nodes, ignoreFilter).insights
        // the value derived for each piece of information from how it is used, ranked within what's shown
        deriveInformationValues([linqset])
        // and for each information asset, across its states
        setAssetValues([linqset.insights], deriveAssetValues([linqset]))
        // update the insights in the model
        linqset.model.insights = linqset.model.insights.filter(i => i.projectKey !== linqset.workspaceKey)
        linqset.model.insights.push(linqset.insights)
    }
}

// joined: the information nodes joined into other models, which have no value or revenue of their own
export const processModelCostsAndValue = (linqset:Linqset, startingNodes:LinqsetNode[], ignoreFilter:boolean, joined:Set<string> = joinedInformationKeys(linqset.edges)) => {
    const {nodes, edges} = linqset

    // the detours items take from the main flow, and the exception work on them, now the items through each node are known
    setExceptionWork(linqset)

    // reset edges
    edges.forEach(e => e.processed = false)

    // traverse graph setting total (cumulative) costs and hours. Also sets costs in info nodes
    startingNodes.forEach(n => {
        setTotalCosts(linqset, new ProcessingEdge(undefined, undefined, n),  nodes, edges, ignoreFilter)
    })

    // reset edges
    edges.forEach(e => e.processed = false)

    const valueNodes = nodes.filter(n => (n.superType === NODE_SUPERTYPE.INFORMATION || n.superType === NODE_SUPERTYPE.OUTCOMES))

    // the per-model breakdown of value arriving from other models is rebuilt below for every node in this model
    nodes.forEach(n => {
        const insights = getInsightsWithoutCreate(n, linqset)
        if (insights) insights.propagatedFromModels = undefined
    })

    // contributions are shared out afresh below, from the value each output and outcome creates in this model
    nodes.forEach(n => {
        const insights = getInsightsWithoutCreate(n, linqset)
        if (insights) {
            insights.contributedValue = 0
            insights.contributedRevenue = 0
            insights.atRiskValue = 0
            insights.atRiskRevenue = 0
        }
    })

    // originValue/originRevenue are accumulated below (own value, other models, realised outcomes) so reset them first
    valueNodes.forEach(n => {
        const insights = getInsightsWithoutCreate(n, linqset)
        if (insights) {
            insights.originValue = 0
            insights.originRevenue = 0
            insights.joinedOut = n.superType === NODE_SUPERTYPE.INFORMATION && joined.has(n._key)
        }
    })

    // set the value for all nodes starting with outputs.
    valueNodes.forEach(n => {
        const insights = getInsightsWithoutCreate(n, linqset)
        if (insights && n.properties && (insights.isOutput || n.superType === NODE_SUPERTYPE.OUTCOMES)) {
            // starting value for the output node. One joined into another model has none of its own: what it's worth
            // comes back from the models it feeds, below
            let value = insights.joinedOut ? 0 : +(n.properties?.outputValue||0)*insights.numberProcessed
            let revenue = insights.joinedOut ? 0 : +(n.properties?.outputRevenue||0)*insights.numberProcessed
            // the value it creates in this model, shared between the work producing it; value from other models is
            // shared out in those
            contributeValue(linqset, n, value, revenue, nodes, edges)
            valueAtRisk(linqset, n, value, revenue, nodes, edges)
            let shares:ValueShares|undefined
            if (n.superType === NODE_SUPERTYPE.INFORMATION) {
                insights.valueFromModels = undefined
                if (n.external) {
                    // add in any value or revenue from other models, remembering where it came from so totals across
                    // models don't count it twice
                    const contributions:ModelValueContributions = {}
                    Object.values(n.insights).filter(i => i.modelKey !== linqset.modelKey && i.modelKey !== ORGANISATION_MODEL_KEY && i.projectKey === linqset.workspaceKey).forEach(i => {
                        if (!i.value && !i.revenue) return
                        contributions[i.modelKey] = {value:i.value||0, revenue:i.revenue||0}
                        value += i.value||0
                        revenue += i.revenue||0
                    })
                    if (Object.keys(contributions).length > 0) {
                        insights.valueFromModels = contributions
                        shares = createValueShares(contributions, value, revenue)
                    }
                }
                // what this output itself originates. Its insights.value may also collect value passed back from
                // other outputs further along the model, which the model totals must not count a second time
                insights.originValue = (insights.originValue||0) + value
                insights.originRevenue = (insights.originRevenue||0) + revenue
                if (value > 0 || revenue > 0) {
                    // add value and revenue onto attached outcomes for reporting
                    edges.filter(e => e.sourceKey === n._key && e.type === EDGE_TYPE.REALISE).forEach(e => {
                        const outcome = linqset.getNode(e.targetKey)
                        if (outcome) {
                            const outcomeInsights = getInsights(outcome, linqset)
                            if (outcomeInsights) {
                                outcomeInsights.value += value
                                outcomeInsights.revenue += revenue
                            }
                        }
                    })
                }
            }
            if (value > 0 || revenue > 0) {
                // add onto this nodes insights
                insights.value += value
                insights.revenue += revenue
                recordShares(insights, value, revenue, shares)
                // send up the model. Value from an outcome counts as originating at the outputs that realise it
                assignValues(linqset, n, insights, value, revenue, nodes, edges, [], n.superType === NODE_SUPERTYPE.OUTCOMES, shares)
            }
        }
    })    
    // sum model values into org insights value for roles and systems
    nodes.filter(n => isRoleSystem(n.superType)).forEach(n => sumModelValuesForOrganisationNode(n, linqset.workspaceKey))
}

/**
 * Sets a role or system's organisation value/revenue from its value in each model. Value it received from model X
 * through a shared output is already counted in its insights for model X when it is used there too, so it is only
 * counted once.
 */
export const sumModelValuesForOrganisationNode = (n:LinqsetNode, workspaceKey:string) => {
    const orgInsights = n.insights.find(i => i.modelKey === ORGANISATION_MODEL_KEY && i.projectKey === workspaceKey)
    if (!orgInsights) return

    orgInsights.value = 0
    orgInsights.revenue = 0

    const modelInsights = n.insights.filter(i => i.modelKey !== ORGANISATION_MODEL_KEY && i.projectKey === workspaceKey)
    // contributions are of the value each model creates itself, so they simply add up across models
    orgInsights.contributedValue = modelInsights.reduce((t, i) => t + (i.contributedValue || 0), 0)
    orgInsights.contributedRevenue = modelInsights.reduce((t, i) => t + (i.contributedRevenue || 0), 0)
    // and so do the values at risk: each model's is of the value it creates
    orgInsights.atRiskValue = modelInsights.reduce((t, i) => t + (i.atRiskValue || 0), 0)
    orgInsights.atRiskRevenue = modelInsights.reduce((t, i) => t + (i.atRiskRevenue || 0), 0)
    const modelKeys = modelInsights.map(i => i.modelKey)
    modelInsights.forEach(i => {
        let value = i.value
        let revenue = i.revenue
        const propagated = i.propagatedFromModels
        if (propagated) {
            Object.keys(propagated).filter(k => modelKeys.indexOf(k) >= 0).forEach(k => {
                value -= propagated[k].value
                revenue -= propagated[k].revenue
            })
        }
        orgInsights.value += value
        orgInsights.revenue += revenue
    })
}

// 1. Sets capacity on roles based on people or its own capacity settings
// 2. Sets hours on people by proportioning hours on roles
// 3. Sets costs on roles and people using hours on roles and role/people costs
// 4. 
export const calculateCostsAndEmissionsForOrganisation = (organisationLinqset:Linqset, models:ModelDetails[], organisation?:Organisation, round:boolean=false):NodeInsights[] => {
    // create insights and populate numbers and hours from models
    resetAllModelInsights(organisationLinqset, models)
    const organisationInsights = organisationLinqset.insights

    const {nodes, edges} = organisationLinqset

    const sums = {infrastructureProportions:createIndexedValues(), peopleProportions:createIndexedValues(), rolePeopleCosts:0, peopleCosts:0, systemInfrastructureCosts:0, infrastructureCosts:0, systemCarbon:0, infrastructureCarbon:0}
    const infrastructureMap:any = {}
    const roleMap:any = {}
    const personMap:any = {}

    const roles = nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE)
    const persons = nodes.filter(n => n.superType === NODE_SUPERTYPE.PEOPLE)
    const systems = nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM)
    const infrastructures = nodes.filter(n => n.superType === NODE_SUPERTYPE.INFRASTRUCTURE)

    organisationInsights.superTypes[NODE_SUPERTYPE.ROLE].number = roles.length
    organisationInsights.superTypes[NODE_SUPERTYPE.ROLE].hours = 0
    organisationInsights.superTypes[NODE_SUPERTYPE.PEOPLE].number = persons.length
    organisationInsights.superTypes[NODE_SUPERTYPE.PEOPLE].hours = 0
    organisationInsights.superTypes[NODE_SUPERTYPE.SYSTEM].number = systems.length
    organisationInsights.superTypes[NODE_SUPERTYPE.SYSTEM].hours = 0
    organisationInsights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].number = infrastructures.length
    organisationInsights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].hours = 0
    organisationInsights.externalOrgs.number = roles.filter(r => r.type === NODE_TYPE.EXTERNAL_ORGANISATION).length
    organisationInsights.externalOrgs.hours = 0

    // the hours a year a full-time person works, for people and roles whose capacity isn't set
    const defaultHours = defaultCapacityHours(organisation)

    // PEOPLE NODES
    // set person capacity and annual costs. Calculate role capacity
    persons.forEach(person => {
        const personInsights = resetNodeForModel(person, organisationLinqset)

        // someone whose time worked isn't set works the organisation's full-time year, if it has one (a team, one each)
        const hoursPerYear = getHoursPerYear(person) || defaultHours*(person.type === NODE_TYPE.TEAM ? (person.properties?.people||1) : 1)
        personInsights.modelledCapacity = hoursPerYear * (person.properties?.percentageModelled||100)/100
        personMap[person._key] = []

        // allocate capacity to roles
        let totalPercentage = 0
        const roleEdges = edges.filter(e => {
            if (e.sourceKey === person._key && e.type === EDGE_TYPE.FULFILS) {
                const role = roles.find(n => n._key === e.targetKey)
                if (role) {
                    totalPercentage += e.outputPercentage
                    if (!roleMap[role._key]) {
                        const roleInsights = resetCostsAndTotalHours(role, organisationLinqset)
                        roleMap[role._key] = {role, roleInsights, persons:[]}
                    }

                    return true
                }                    
            }
            return false
        })
    
        roleEdges.forEach(e => {
            if (roleMap[e.targetKey]) {
                // Calculate proportion of this persons time they spend on this role
                // if percentages not set even time on each
                const proportionOfCapacity = totalPercentage?e.outputPercentage/totalPercentage:1/roleEdges.length
                personMap[person._key].push({insights:roleMap[e.targetKey].roleInsights, proportion:proportionOfCapacity})

                // we are going to record the people node info so can be used to allocated hours and costs later
                roleMap[e.targetKey].roleInsights.modelledCapacity += proportionOfCapacity*personInsights.modelledCapacity
                roleMap[e.targetKey].roleInsights.numPeople += person.properties?.people||1
                roleMap[e.targetKey].roleInsights.hasPeople = true

                roleMap[e.targetKey].persons.push({person, cost:0, insights:personInsights, proportionOfCapacity})
            }
        })
    })
    // ROLE NODES
    // Set costs on roles using role settings or persons
    roles.forEach(role => {    
        let roleInsights:NodeInsights = roleMap[role._key] && roleMap[role._key].roleInsights
        if (roleInsights) {
            // use people settings
            roleMap[role._key].persons.forEach((pr:any) => {
                const personRoleCapacity = pr.insights.modelledCapacity*pr.proportionOfCapacity
    
                // proportionOfRole is this persons proportion of the role based on capacity
                pr.proportionOfRole = roleInsights.modelledCapacity?personRoleCapacity/roleInsights.modelledCapacity:1/roleMap[role._key].persons.length

                // record the proportion of this role this person does. Is used in insights calcs for convenience
                addToIndexValue(roleInsights.proportionOfPersonInRole, pr.person._key, pr.proportionOfRole)

                // allocate hours from the role to the person based on proportion this person provides capacity to the role
                pr.hours = (roleInsights.hours||0)*pr.proportionOfRole
    
                // allocate hours to person based on this proportion
                pr.insights.hours += pr.hours
                addToIndexValue(sums.peopleProportions, role._key, pr.proportionOfRole)
            })  
        }else {
            // uses role settings, ignoring people
            // we have hours set for each model so total them up and then set costs at org level based on that 
            roleInsights = resetCostsAndTotalHours(role, organisationLinqset)
            roleInsights.numPeople = role.properties?.people||1

            const capacityUnset = !role.properties?.frequency || role.properties.frequency.type === FREQUENCY_TYPE.USE_CALCULATED
            if (role.type === NODE_TYPE.EXTERNAL_ORGANISATION || (capacityUnset && !defaultHours)) {
                // an external organisation's capacity isn't the organisation's to manage, and without a default a role
                // with no capacity set is taken to have just enough for its work
                roleInsights.modelledCapacity = roleInsights.hours
            }else if (capacityUnset) {
                // a full-time year for each of its people, as the organisation sets it
                roleInsights.modelledCapacity = (role.properties?.people||1)*defaultHours*(role.properties?.percentageModelled||100)/100
            }else {
                // costs and capacity are set on the role itself so ignore people
                const capacity = getHoursPerYear(role)
                roleInsights.modelledCapacity = capacity*(role.properties?.percentageModelled||100)/100
            }

            roleInsights.costAllocation = getCost(role, roleInsights, roleInsights.hours, role.properties?.people||1)
        }
    })    

    // persons have hours set, so now set costs on persons
    persons.filter(person => person.properties).forEach(person => {
        const insights = getInsights(person, organisationLinqset)
        insights.numPeople = person.properties?.people||1
        insights.costAllocation = getCost(person, insights, insights.hours, insights.numPeople)
         
        sums.peopleCosts += insights.costAllocation.allocated

        // allocate person costs to roles
        personMap[person._key].forEach((r:any) => {
            const c = r.proportion*insights.costAllocation.allocated
            addToIndexValue(r.insights.allocatedCostsByType, NODE_SUPERTYPE.PEOPLE, c)
            sums.rolePeopleCosts += c
        })
    })

    let totalInfrastructureHours = 0
    const roleByKey = new Map(roles.map(r => [r._key, r]))
    // the roles that use each system in the work modelled, for the people who use it
    const systemUsers = new Map<string, LinqsetNode[]>()

    // SYSTEM NODES
    // Set system costs and emissions based on system hours
    systems.forEach(system => {
        // we have hours set for each model so total them up and then set costs at org level based on that
        const systemOrganisationInsights = resetCostsAndTotalHours(system, organisationLinqset)
        // the people who use it, which a cost per person is for: those in the roles that use it in the work modelled
        // (drawn in the models, not the organisation model), with any drawn using it on the organisation model, each
        // person once however many of those roles they are in. People in external organisations aren't counted, as what
        // they use isn't the organisation's to pay for
        const users = Object.keys(systemOrganisationInsights.timeUsedByRole?.map || {})
            .map(k => roleByKey.get(k))
            .filter((r):r is LinqsetNode => !!r && r.type !== NODE_TYPE.EXTERNAL_ORGANISATION)
        systemUsers.set(system._key, users)
        systemOrganisationInsights.numPeople = countPeople([system, ...users], organisationLinqset)

        // for systems we use time each role uses this system to proportion role capacity to the system
        if (systemOrganisationInsights.timeUsedByRole) {
            Object.keys(systemOrganisationInsights.timeUsedByRole.map).forEach(k => {
                // add on time spent executing actions without a role
                if (k === NO_ROLE) {
                    systemOrganisationInsights.modelledCapacity += getIndexValue(systemOrganisationInsights.timeUsedByRole, NO_ROLE)
                }else {
                    const role = roles.find(r => r._key === k)
                    if (role) {
                        const roleInsights = getInsightsWithoutCreate(role, organisationLinqset)
                        if (roleInsights && roleInsights.hours) {
                            // proportion of time role spends on this system
                            const proportion = getIndexValue(systemOrganisationInsights.timeUsedByRole, k)/roleInsights.hours
                            systemOrganisationInsights.modelledCapacity += roleInsights.modelledCapacity*proportion
                        }
                    }
                }
            })
        }
        
        // set system costs
        if (system.properties) {
            systemOrganisationInsights.costAllocation = getCost(system, systemOrganisationInsights, systemOrganisationInsights.hours, systemOrganisationInsights.numPeople)
        }
        
        // process attached infrastructure nodes
        edges.filter(e => e.targetKey === system._key && e.type === EDGE_TYPE.PROVIDES).forEach(infrastructureEdge => {
            const infrastructure = infrastructures.find(n => n._key === infrastructureEdge.sourceKey)

            if (infrastructure) {
                
                if (!infrastructureMap[infrastructure._key]) {
                    const infrastructureInsights = resetNodeForModel(infrastructure, organisationLinqset)
                    infrastructureMap[infrastructure._key] = {infrastructure, insights:infrastructureInsights, systemNodes:[]}
                }

                // remember relationship for below to make more efficient
                infrastructureMap[infrastructure._key].insights.hours += systemOrganisationInsights.hours
                infrastructureMap[infrastructure._key].insights.modelledCapacity += systemOrganisationInsights.modelledCapacity
    
                infrastructureMap[infrastructure._key].systemNodes.push(system)
                totalInfrastructureHours += systemOrganisationInsights.hours
            }
        })
    })

    // INFRASTRUCTURE NODES
    // now calculate infrastructure costs and allocate to systems
    infrastructures.forEach(infrastructure => {
        if (infrastructureMap[infrastructure._key]) {
            const infrastructureInsights = infrastructureMap[infrastructure._key].insights
            infrastructureInsights.proportion = totalInfrastructureHours?infrastructureInsights.hours/totalInfrastructureHours:0
            // the people who use the systems it provides, each once
            const users = infrastructureMap[infrastructure._key].systemNodes.flatMap((s:LinqsetNode) => systemUsers.get(s._key) || [])
            infrastructureInsights.numPeople = countPeople([infrastructure, ...users], organisationLinqset)
            infrastructureInsights.emissions = organisation?getCarbonEmissions(infrastructure, infrastructureInsights, organisation, infrastructureInsights.numPeople):[]
            infrastructureInsights.costAllocation = getCost(infrastructure, infrastructureInsights, infrastructureInsights.hours, infrastructureInsights.numPeople)
            sums.infrastructureCosts += infrastructureInsights.costAllocation.allocated

            infrastructureMap[infrastructure._key].systemNodes.forEach((system:LinqsetNode) => {
                const systemInsights = getInsights(system, organisationLinqset)

                // system hours as a proportion of total infrastructure hours
                const proportion = infrastructureInsights.hours?systemInsights.hours/infrastructureInsights.hours:1/infrastructureMap[infrastructure._key].systemNodes.length
                // allocate proportion of infrastructure costs to system costs and emissions

                // allocated proportion of people costs to role
                const c = proportion*infrastructureInsights.costAllocation.allocated

                addToIndexValue(systemInsights.allocatedCostsByType, NODE_SUPERTYPE.INFRASTRUCTURE, c)
                sums.systemInfrastructureCosts += c

                allocateEmissions(infrastructureInsights.emissions, systemInsights.allocatedCarbonByScope, proportion)
                addToIndexValue(sums.infrastructureProportions, infrastructure._key, proportion)
            })
        }
    })

    checkEquality(sums.rolePeopleCosts, sums.peopleCosts, organisationInsights.processingErrors, `People costs on roles does not equal costs on people`)
    checkEquality(sums.systemInfrastructureCosts, sums.infrastructureCosts, organisationInsights.processingErrors, `Infrastructure costs on systems does not equal costs on infrastructure`)

    Object.keys(sums.infrastructureProportions.map).forEach(k => {
        checkEquality(getIndexValue(sums.infrastructureProportions, k), 1, organisationInsights.processingErrors, `Allocations to systems for infrastructure called ${infrastructureMap[k].infrastructure.name} does not add to 100%`)
    })
    Object.keys(sums.peopleProportions.map).forEach(k => {
        checkEquality(getIndexValue(sums.peopleProportions, k), 1, organisationInsights.processingErrors, `Allocations to people to role called ${roleMap[k].role.name} does not add to 100%`)
    })

    // We have set organisation costs, emissions and capacity on organisation nodes. Organisation model insights have also been set.
    // Distribute organisation costs to models
    const organisationNodeInsights = distributeOrganisationCostsToModels(organisationLinqset, models, round)   
    roundModelInsights(organisationLinqset.insights)
    models.forEach(m => {
        const modelInsights = m.insights.find(mi => mi.projectKey === organisationLinqset.workspaceKey)
        if (modelInsights) roundModelInsights(modelInsights)
    })
    return organisationNodeInsights
}

const distributeOrganisationCostsToModels = (organisationLinqset:Linqset, models:ModelDetails[], round:boolean):NodeInsights[] => {
    const orgInsights = organisationLinqset.insights

    orgInsights.numModels = models.length

    const organisationNodeInsights:NodeInsights[] = []

    // distribute node organisation details across node model insights and total these into model insights
    organisationLinqset.nodes.forEach(n => {
        const nodeOrgInsights = getInsightsWithoutCreate(n, organisationLinqset)
        if (nodeOrgInsights) {
            organisationNodeInsights.push(nodeOrgInsights)
            // add node details to organisation model insights
            addNodeInsightsToModel(n, nodeOrgInsights, orgInsights)
            // set the costs and carbon for the node from emissions and allocted costs
            if (nodeOrgInsights.costAllocation) setIndexValue(nodeOrgInsights.allocatedCostsByType, n.superType, nodeOrgInsights.costAllocation.allocated)

            if (nodeOrgInsights.allocatedCarbonByScope) allocateEmissions(nodeOrgInsights.emissions, nodeOrgInsights.allocatedCarbonByScope, 1)

            // distribute role and system insights across models they belong to
            const insights = n.insights.filter(i => i.projectKey === organisationLinqset.workspaceKey && models.find(m => m._key === i.modelKey))
           
            if (isRoleSystem(n.superType)) {
                if (n.superType === NODE_SUPERTYPE.ROLE && n.type !== NODE_TYPE.EXTERNAL_ORGANISATION && insights.length===0) {
                    orgInsights.unusedCapacity += nodeOrgInsights.modelledCapacity
                }
                insights.forEach(nodeModelInsights => {
                    resetNodeCostsForModel(nodeModelInsights)
                    nodeModelInsights.numPeople = nodeOrgInsights.numPeople
                    nodeModelInsights.hasPeople = nodeOrgInsights.hasPeople
        
                    nodeModelInsights.proportion = nodeOrgInsights.hours?nodeModelInsights.hours/nodeOrgInsights.hours:1/insights.length
                    
                    // set model costs, capacity and emissions based on hours (proportion)
                    if (nodeModelInsights.proportion > 0) {
                        nodeModelInsights.modelledCapacity = nodeModelInsights.proportion*nodeOrgInsights.modelledCapacity                        
                        addIndexedValues(nodeOrgInsights.allocatedCostsByType, nodeModelInsights.allocatedCostsByType, nodeModelInsights.proportion)

                        if (n.superType === NODE_SUPERTYPE.SYSTEM) {
                            if (!nodeModelInsights.allocatedCarbonByScope) nodeModelInsights.allocatedCarbonByScope = createIndexedValues()
                            addIndexedValues(nodeOrgInsights.allocatedCarbonByScope, nodeModelInsights.allocatedCarbonByScope, nodeModelInsights.proportion)
                        }
                    }
                    const model = models.find(m => m._key === nodeModelInsights.modelKey)
                    if (model) {
                        const modelInsights = model.insights.find(mi => mi.projectKey === organisationLinqset.workspaceKey)
                        if (modelInsights) addNodeInsightsToModel(n, nodeModelInsights, modelInsights, model._key)
                    }
                    if (round) roundNodeInsights(nodeModelInsights)
                })
            }
            roundNodeInsights(nodeOrgInsights)
        }
    })    
    return organisationNodeInsights
}

// the outputs that realise an outcome: what they produce is counted at the outcome, so isn't counted again
export const outputsRealisingOutcomes = (edges:LinqsetEdge[]) => new Set(edges.filter(e => e.type === EDGE_TYPE.REALISE).map(e => e.sourceKey))

const addNodeInsightsToModel = (node:LinqsetNode, nodeInsights:NodeInsights, modelInsights:ModelInsights, modelKey?:string, realising:Set<string> = new Set()) => {
    const {superType} = node
    // an external organisation's time and capacity aren't the organisation's own: they're reported apart (externalOrgs,
    // below), not in the roles'
    const external = node.type === NODE_TYPE.EXTERNAL_ORGANISATION

    const insightsTotal:InsightTotal = modelInsights.superTypes[superType]

    insightsTotal.percentageModelled += (node.properties?.percentageModelled||100)*nodeInsights.proportion

    if (!modelKey) {
        // adding to organisation model insights
        if (isModelNode(superType)) return
        if (!external) insightsTotal.hours += nodeInsights.hours

        // for organisation model insights: add this nodes cost allocations to the model totals for this super type
        let allocation = modelInsights.costAllocations.find(a => a.superType === node.superType)
        if (!allocation) {
            allocation = createCostAllocation(node.superType)
            modelInsights.costAllocations.push(allocation)
        }
        addAllocations(nodeInsights.costAllocation, allocation)
        insightsTotal.allocatedCost += nodeInsights.costAllocation.allocated

        // add emissions from infrastructure to the models total emissions
        // also add them to allocatedcarbonByScope for systems and infrastructure
        if (superType === NODE_SUPERTYPE.INFRASTRUCTURE && nodeInsights.emissions) {
            if (!modelInsights.emissions) modelInsights.emissions = []
            addEmissions(nodeInsights.emissions, modelInsights.emissions)
            allocateEmissions(nodeInsights.emissions, modelInsights.allocatedCarbonByScope, 1)
        }
        if (superType === NODE_SUPERTYPE.ROLE && !external) {
            // add capacity if not using people for capacity
            insightsTotal.capacity += nodeInsights.modelledCapacity
            modelInsights.modelledCapacity += nodeInsights.modelledCapacity
    
            const total = convertModelledCapacityToTotal(nodeInsights.modelledCapacity, node.properties?.percentageModelled)
            modelInsights.unModelledCapacity += (total-nodeInsights.modelledCapacity)
        }
    
    }else {
        // adding to model specific insights
        //if (node.superType === NODE_SUPERTYPE.INFORMATION && node.external && node.modelKey !== modelKey) return
        if (node.superType === NODE_SUPERTYPE.ACTION) {
            const actionType = nodeInsights.actionType
            if (!modelInsights.actionTypes[actionType]) modelInsights.actionTypes[actionType] = createActionType()
            modelInsights.actionTypes[actionType].number++
            modelInsights.actionTypes[actionType].processed += nodeInsights.numberProcessed
            modelInsights.actionTypes[actionType].hours += nodeInsights.hours
            modelInsights.actionTypes[actionType].cost += nodeInsights.allocatedCostsByType?nodeInsights.allocatedCostsByType.total:0

            // exception work is friction, counted beside the dead ends
            if (nodeInsights.exceptionWork) {
                modelInsights.exceptionActions++
                modelInsights.exceptionCost += nodeInsights.allocatedCostsByType?.total || 0
                modelInsights.exceptionTime += nodeInsights.hours || 0
                modelInsights.exceptionCarbon += nodeInsights.allocatedCarbonByScope?.total || 0
            }else if (nodeInsights.workKind === WORK_TYPE.NOT_VALUE_ADDING) {
                // and so is other work marked as not adding value
                modelInsights.wasteCost = (modelInsights.wasteCost || 0) + (nodeInsights.allocatedCostsByType?.total || 0)
                modelInsights.wasteTime = (modelInsights.wasteTime || 0) + (nodeInsights.hours || 0)
            }

            if (node.properties?.tagKeys) {
                Object.keys(node.properties.tagKeys).forEach((k:any) => {
                    addToNodeGroupInsights(modelInsights.tags, node.properties?.tagKeys[k], nodeInsights)
                })
            }
        }
        
        if (!external) insightsTotal.hours += nodeInsights.hours
        insightsTotal.number++

        if (isRoleSystem(node.superType) && nodeInsights.allocatedCostsByType) {
            // add costs to the model insights
            Object.keys(nodeInsights.allocatedCostsByType.map).forEach(t => {
                modelInsights.superTypes[t].allocatedCost += nodeInsights.allocatedCostsByType.map[t]
            })
            if (node.superType === NODE_SUPERTYPE.SYSTEM) {
                // add carbon to the model insights
                if (nodeInsights.allocatedCarbonByScope) {
                    addIndexedValues(nodeInsights.allocatedCarbonByScope, modelInsights.allocatedCarbonByScope)
                }
            }
        }
        if (superType === NODE_SUPERTYPE.ROLE && !external) {
            // add capacity to totals
            insightsTotal.capacity += nodeInsights.modelledCapacity
            modelInsights.modelledCapacity += nodeInsights.modelledCapacity
    
            const total = convertModelledCapacityToTotal(nodeInsights.modelledCapacity, node.properties?.percentageModelled)
            modelInsights.unModelledCapacity += (total-nodeInsights.modelledCapacity)
        }   

        if (superType === NODE_SUPERTYPE.INFORMATION) {
            // do not include any inputs from other models
            if (node.modelKey === modelKey) {        
                if (nodeInsights.isOutput) {
                    modelInsights.numOutputs++
                    // what the work produces: its outcomes (see below), and the outputs at the end of a chain that
                    // realise none
                    if (!realising.has(node._key)) modelInsights.numOutputsProcessed += nodeInsights.numberProcessed
                    // count only the value each output originates, so value passed between outputs isn't counted twice
                    modelInsights.outputValue += nodeInsights.originValue ?? nodeInsights.value
                    modelInsights.outputRevenue += nodeInsights.originRevenue ?? nodeInsights.revenue
                    if (nodeInsights.valueFromModels) {
                        if (!modelInsights.valueFromModels) modelInsights.valueFromModels = {}
                        addValueContributions(nodeInsights.valueFromModels, modelInsights.valueFromModels)
                    }
                    if (isFrictionOutput(nodeInsights)) {
                        modelInsights.frictionCost += nodeInsights.totalAllocatedCostsByType.total
                        modelInsights.frictionCarbon += nodeInsights.totalCarbon
                        modelInsights.frictionTime += nodeInsights.totalHours
                    }
                }
                if (node.informationAssetKey) {
                    addToNodeGroupInsights(modelInsights.informationAssets, node.informationAssetKey, nodeInsights)
                }
            }
        }

        if (superType === NODE_SUPERTYPE.OUTCOMES && node.modelKey === modelKey) {
            modelInsights.numOutputsProcessed += nodeInsights.numberProcessed||0
        }

        if (nodeInsights.isInput) {
            modelInsights.numInputsProcessed += (nodeInsights.numberProcessed||0)
            if (node.superType === NODE_SUPERTYPE.ACTION) {
                modelInsights.numInputsExternal += (nodeInsights.numberProcessed||0)
            }
        }
    }

    if (superType === NODE_SUPERTYPE.SYSTEM) {
        modelInsights.infoSuppliedBySystems += nodeInsights.numInfoSupplied
    }
    if (node.type === NODE_TYPE.EXTERNAL_ORGANISATION) {
        modelInsights.externalOrgs.allocatedCost += nodeInsights.allocatedCostsByType.total
        modelInsights.externalOrgs.hours += nodeInsights.hours
        modelInsights.externalOrgs.capacity += nodeInsights.modelledCapacity
    }
}


export const setCostsOnActions = (linqset:Linqset, actionsMap:any, ignoreFilter:boolean) => {
    // allocate capacity, costs and emissions to actions
    linqset.nodes.filter(psn => isRoleSystem(psn.superType) && (ignoreFilter || !psn.excluded) && actionsMap[psn._key] && actionsMap[psn._key].length>0).forEach(psn => {  
        const psnModelInsights = getInsights(psn, linqset)   

        // now allocate costs to the action nodes
        actionsMap[psn._key].forEach((actionNodeKey:string) => {
            const actionNode = linqset.getNode(actionNodeKey)
            if (actionNode) {
                const actionInsights = getInsights(actionNode, linqset)

                // this action time as a proportion of overall system/role time in this model
                const proportion = psnModelInsights.hours>0?actionInsights.hours/psnModelInsights.hours:1/actionsMap[psn._key].length
                if (proportion > 0) {
                    // add proportion of costs and carbon to action
                    addIndexedValues(psnModelInsights.allocatedCostsByType, actionInsights.allocatedCostsByType, proportion)
                    // add this role/systems costs to actions allocatin costs by node
                    addToIndexValue(actionInsights.allocatedCostsByNode, psn._key, psnModelInsights.allocatedCostsByType.total*proportion)

                    if (psn.superType === NODE_SUPERTYPE.SYSTEM) {
                        addIndexedValues(psnModelInsights.allocatedCarbonByScope, actionInsights.allocatedCarbonByScope, proportion)
                    }else {
                        const c = psnModelInsights.modelledCapacity*proportion

                        if (actionInsights.numPeople === 0 || actionInsights.modelledCapacity > c) {
                            // if multiple roles attached to this action we take the lowest capacity
                            actionInsights.modelledCapacity = c
                            actionInsights.numPeople = psnModelInsights.numPeople
                            actionInsights.roleKey = psn._key
                        }
                    }
                }
                if (psn.superType === NODE_SUPERTYPE.SYSTEM) {
                    if (actionInsights.actionType === ACTION_TYPE.MANUAL)
                        actionInsights.actionType = ACTION_TYPE.BOTH
                    else if (actionInsights.actionType === ACTION_TYPE.NONE)
                        actionInsights.actionType = ACTION_TYPE.AUTOMATED
                }else {
                    if (psn.type === NODE_TYPE.EXTERNAL_ORGANISATION) {
                        if (actionInsights.actionType === ACTION_TYPE.NONE) actionInsights.actionType = ACTION_TYPE.EXTERNAL
                    }else if (actionInsights.actionType === ACTION_TYPE.AUTOMATED)
                        actionInsights.actionType = ACTION_TYPE.BOTH
                    else if (actionInsights.actionType === ACTION_TYPE.NONE || actionInsights.actionType === ACTION_TYPE.EXTERNAL)
                        actionInsights.actionType = ACTION_TYPE.MANUAL
                }
            }
        })
    })
}

// Total the action hours and number processed on the system and role nodes
export const setTimeForPeopleAndSystems = (linqset:Linqset, ignoreFilter:boolean) => {
    const actionsMap:any = {}
    linqset.insights = createModelInsights(linqset.organisationKey, linqset.workspaceKey, linqset.modelKey)

    // now set hours for each system, and role
    linqset.nodes.filter(n => (ignoreFilter || !n.excluded)).forEach(n => {
        if (isRoleSystem(n.superType)) {
            // get all action nodes attached to this people or system node
            actionsMap[n._key] = setActionHoursForRoleSystem(n, linqset, ignoreFilter)
        }
        const nodeInsights = getInsights(n, linqset)
        linqset.insights.superTypes[n.superType].number++
        if (n.type !== NODE_TYPE.EXTERNAL_ORGANISATION) linqset.insights.superTypes[n.superType].hours += nodeInsights.hours
        if (n.type === NODE_TYPE.EXTERNAL_ORGANISATION) {
            linqset.insights.externalOrgs.number++
            linqset.insights.externalOrgs.hours += nodeInsights.hours
        }
    })
    return actionsMap
}

export const setTimeForActionsAndInfo = (linqset:Linqset, startingNodes:LinqsetNode[], ignoreFilter:boolean) => {
    const {nodes, edges} = linqset

    // set all node insights to zero for this model
    linqset.nodes.forEach(n => {
        resetNodeForModel(n, linqset)
    })
    
    // reset edges
    edges.forEach(e => e.processed = false)

    // traverse model setting action and info number processed and hours
    startingNodes.forEach(n => {
        if (n.superType === NODE_SUPERTYPE.INFORMATION) {

            // every connection but a used-in one (which passes information without items) carries its items on
            const nextEdges = edges.filter(e => e.sourceKey === n._key && e.type !== EDGE_TYPE.USED_IN)
            const totalPercentage = nextEdges.reduce((t, e) => {
                // 100% when not set; 0% is a choice (none of the items go that way), so it stays
                if (e.outputPercentage === undefined || e.outputPercentage === null || Number.isNaN(e.outputPercentage)) e.outputPercentage = 100
                t += e.outputPercentage
                return t
            }, 0)

            if (nextEdges.length > 0) {
                // information nodes that injects into this model. 
                // process the actions that come off this node
                if (!linqset.isFilterModel() && n.modelKey !== linqset.modelKey && n.external) {
                    // external node so create insights in this model and copy over number processed
                    const ni = n.insights.find(i => i.modelKey === n.modelKey && i.projectKey === linqset.workspaceKey)
                    if (ni) {
                        const i = getInsights(n, linqset)
                        i.numberProcessed = ni.numberProcessed
                        i.isInput = true
                        setIndexValue(i.inputProportions, n._key, 1)
                    }
                }

                nextEdges.forEach(e => {
                    const nextNode = nodes.find(nn => nn._key === e.targetKey)
                    if (nextNode && nextNode.superType === NODE_SUPERTYPE.ACTION) {
                        // a share for distributing hours, cost and value: with every output at 0%, an equal share each (the items
                        // passed are each output's own percentage, worked out where they arrive)
                        e.proportion = totalPercentage>0?e.outputPercentage/totalPercentage:1/nextEdges.length
                        e.processed = true
                        if (linqset.isFilterModel() || n.modelKey === linqset.modelKey) {
                            const i = getInsights(nextNode, linqset)
                            i.isInput = true
                            setIndexValue(i.inputProportions, nextNode._key, 1)
                        }

                        setHoursForNode(linqset, nextNode, nodes, edges, ignoreFilter)    
                    }
                })
            }
        }else {
            const nodeInsights = getInsights(n, linqset)
            nodeInsights.isInput = true
            setHoursForNode(linqset, n, nodes, edges, ignoreFilter)
        }
    })
}

export const getStartingNodes = (nodes:LinqsetNode[], edges:LinqsetEdge[]) => {
    return nodes.filter(n => {
        if (n.superType !== NODE_SUPERTYPE.INFORMATION && n.superType !== NODE_SUPERTYPE.ACTION) return false
        const superType = n.superType === NODE_SUPERTYPE.ACTION?NODE_SUPERTYPE.INFORMATION:NODE_SUPERTYPE.ACTION
        return edges.filter(e => e.targetKey === n._key && nodes.find(ln => ln._key === e.sourceKey && ln.superType === superType)).length === 0
    })
}

// process this node. Could be an action or information node. Will recursively traverse the graph
const setHoursForNode = (linqset:Linqset, node:LinqsetNode, nodes:LinqsetNode[], edges:LinqsetEdge[], ignoreFilter:boolean) => {
    const superType = (node.superType === NODE_SUPERTYPE.ACTION || node.superType === NODE_SUPERTYPE.OUTCOMES)?NODE_SUPERTYPE.INFORMATION:NODE_SUPERTYPE.ACTION
    // get all edges to or from node
    const nodeEdges = edges.filter(e => e.type !== EDGE_TYPE.USED_IN && (e.sourceKey === node._key || e.targetKey === node._key))
    const insights = getInsights(node, linqset)

    // incoming edges from super type and are not used in edges
    const incomingEdges = nodeEdges.filter(e => (e.targetKey === node._key)).map(e => {
        const en = nodes.find(n => n._key === e.sourceKey)
        return new ProcessingEdge(en, e, node)
    })

    if (!incomingEdges.find(e => e.edge && !e.edge.processed && e.source && e.source.superType === superType)) {
        // all incoming edges have been processed so we will move forward. If any edges were still to be processed we stop and let the others come in.
        // first calculate the number processed for this node
        setTimeForNode(linqset, node, incomingEdges, ignoreFilter)

        const outgoingEdges = nodeEdges.filter(e => e.sourceKey === node._key && (linqset.isFilterModel() || e.type !== EDGE_TYPE.JOINS) && !e.processed).map(e => {
            const en = nodes.find(n => n._key === e.targetKey)
            return new ProcessingEdge(node, e, en)
        })
        insights.isLastNode = outgoingEdges.length === 0

        // calc total percentage down each edge so can proportion load across them below.
        const filteredEdges = outgoingEdges.filter(e => e.edge && e.target && (e.target.superType === superType || e.target.superType === NODE_SUPERTYPE.OUTCOMES))
        const totalPercentage = filteredEdges.reduce((t, e) => {
            if (e.edge) {
                // 100% when not set; 0% is a choice (none of the items go that way), so it stays
                if (e.edge.outputPercentage === undefined || e.edge.outputPercentage === null || Number.isNaN(e.edge.outputPercentage)) e.edge.outputPercentage = 100
                t += e.edge.outputPercentage
            }
            return t
        }, 0)
        insights.numOutgoingEdges = filteredEdges.length

        filteredEdges.forEach(e => {
            if (e.edge) {
                e.edge.processed = true
                // set proportions for outgoing edges. These are used to calculate the next nodes numbers
                // a share for distributing hours, cost and value: with every output at 0%, an equal share each (the items
                // passed are each output's own percentage, worked out where they arrive)
                e.edge.proportion = totalPercentage>0?e.edge.outputPercentage/totalPercentage:1/filteredEdges.length
                if (e.target) setHoursForNode(linqset, e.target, nodes, edges, ignoreFilter)
            }
        })
    }
}

// calculates the number processed and hours for this node
const setTimeForNode = (linqset:Linqset, node:LinqsetNode, incomingEdges:ProcessingEdge[], ignoreFilter:boolean) => {
    const superType = (node.superType === NODE_SUPERTYPE.ACTION || node.superType === NODE_SUPERTYPE.OUTCOMES)?NODE_SUPERTYPE.INFORMATION:NODE_SUPERTYPE.ACTION

    const type = node.superType === NODE_SUPERTYPE.INFORMATION?ACTION_FREQUENCY_TYPE.SUM:(node.properties?.actionProcessType || ACTION_FREQUENCY_TYPE.SUM)
    let number:number = 0
    let hours = 0
    const insights = getInsights(node, linqset)

    const inputs = createIndexedValues()

    const previousNodeContributions:any = {}        // map indexed by ndde key of the number of items coming from previous nodes

    // first determine the number coming into this node
    const filteredEdges = incomingEdges.filter(e => e.source && e.source.superType === superType)
    if (filteredEdges.length > 0) {        
        filteredEdges.forEach((e, i) => {
            const previousNode = e.source
            if (previousNode) {
                const previousInsights = getInsights(previousNode, linqset)

                // proportion of items processed passed down this edge
                const proportion = e.edge?e.edge.outputPercentage/100:0

                const nodeNumber = previousInsights.numberProcessed*proportion
                              
                hours += e.edge?e.edge.proportion*previousInsights.hours:0

                if (type === ACTION_FREQUENCY_TYPE.SUM || type === ACTION_FREQUENCY_TYPE.AVERAGE) {
                    number += nodeNumber
                }else if (type == ACTION_FREQUENCY_TYPE.MAXIMUM && number < nodeNumber) {
                    number = nodeNumber
                }else if (i===0 || (type == ACTION_FREQUENCY_TYPE.MINIMUM && number > nodeNumber)) {
                    number = nodeNumber
                }

                previousNodeContributions[previousNode._key] = nodeNumber

                // add up the proportions contributed by each input node
                Object.keys(previousInsights.inputProportions.map).forEach(s => {
                    if (!inputs.map[s]) inputs.map[s] = 0

                    inputs.map[s] += previousInsights.inputProportions.map[s]*nodeNumber
                })
                inputs.total += nodeNumber        
            }
        })
        if (number && type===ACTION_FREQUENCY_TYPE.AVERAGE) {
            number = number/filteredEdges.length
        }
    }
    
    // calculated is calculated from the previous nodes starting with the first one that set a frequency, never uses the overridden value.
    insights.calculatedNumberProcessed = number
    
    if (node.superType === NODE_SUPERTYPE.ACTION && node.properties?.frequency && node.properties.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED && node.properties.frequency.ocurrences !== undefined && node.properties.frequency.ocurrences >= 0) {   
        // they have set a frequency so use that instead
        insights.numberProcessed = getTimesPerYear(node.properties)
    }else {
        // use the calculated value.
        insights.numberProcessed = number
    }  
    if (node.superType !== NODE_SUPERTYPE.OUTCOMES && (ignoreFilter || !node.excluded)) {
        insights.hours = (node.properties && node.superType === NODE_SUPERTYPE.ACTION)?(getHoursPerItem(node.properties) * insights.numberProcessed):hours
        insights.waitHours = 0
        if (node.superType === NODE_SUPERTYPE.ACTION && isWait(node, linqset.edges)) {
            // nobody works on a step where items wait: its duration is how long they wait, which isn't hours of work
            insights.waitHours = insights.hours
            insights.hours = 0
        }
        if (node.superType === NODE_SUPERTYPE.ACTION) {
            // default capacity to equal hours. gets updated if has a role later
            insights.modelledCapacity = insights.hours
        }
    }

    insights.inputProportions = createIndexedValues()
    insights.previousNodeProportions = createIndexedValues()

    if (insights.isInput) {
        // start node so set the starting node proportion to 1
        setIndexValue(insights.inputProportions, node._key, 1)
    }else {
        const keys = Object.keys(inputs.map)

        keys.forEach(s => {
            insights.inputProportions.map[s] = inputs.total?inputs.map[s]/inputs.total:1/keys.length
            insights.inputProportions.total += insights.inputProportions.map[s]
        })

        const pKeys = Object.keys(previousNodeContributions)

        pKeys.forEach(k => {
            if (type === ACTION_FREQUENCY_TYPE.SUM && number > 0) {
                // based on number each node contributed. With none arriving (an action with a frequency of its own
                // can still create items) each contributes evenly, as 0/0 would pass NaN on as value
                insights.previousNodeProportions.map[k] = previousNodeContributions[k]/number
            }else {
                // evenly proportion the contribution
                insights.previousNodeProportions.map[k] = 1/filteredEdges.length
            }
            insights.previousNodeProportions.total += insights.previousNodeProportions.map[k]
        })
    }
}

const setActionHoursForRoleSystem = (psn:LinqsetNode, linqset:Linqset, ignoreFilter:boolean) => {
    const psnInsights = getInsights(psn, linqset)
    const actionsSupported:string[] = []

    if (psn.superType === NODE_SUPERTYPE.SYSTEM) {
        psnInsights.timeUsedByRole = createIndexedValues()
        setIndexValue(psnInsights.timeUsedByRole, NO_ROLE, 0)
    }

    linqset.edges.filter(edge => edge.sourceKey === psn._key).forEach(edge => {
        const node = linqset.nodes.find(n => n._key === edge.targetKey)
        if (node && (ignoreFilter || !node.excluded)) {
            if (node.superType === NODE_SUPERTYPE.ACTION) {
                const actionInsights = getInsights(node, linqset)
                actionsSupported.push(node._key)
                psnInsights.hours += actionInsights.hours
                psnInsights.numberProcessed += actionInsights.numberProcessed
        
                if (psn.superType === NODE_SUPERTYPE.SYSTEM) {
                    // system connected to an action
                    addToIndexValue(psnInsights.timeUsedByRole, NO_ROLE, actionInsights.hours)
                }
            }else if (node.superType === NODE_SUPERTYPE.ROLE) {
                // node is connected to a role node so need to find the action node that instance (visualisation) is
                // connected to. An edge without a visualisation key can't be matched: missing keys would match each other
                const roleEdge = edge.targetVisualisationKey && linqset.edges.find(e => e.sourceKey === node._key && e.type === EDGE_TYPE.PERFORM && e.sourceVisualisationKey === edge.targetVisualisationKey)
                if (roleEdge) {
                    const action = linqset.nodes.find(n => n._key === roleEdge.targetKey && n.superType === NODE_SUPERTYPE.ACTION)
                    if (action && (ignoreFilter || !action.excluded)) {
                        const actionInsights = getInsights(action, linqset)
                        actionsSupported.push(action._key)
                        psnInsights.hours += actionInsights.hours
                        psnInsights.numberProcessed += actionInsights.numberProcessed
                        addToIndexValue(psnInsights.timeUsedByRole, node._key, actionInsights.hours)
                    }
                }
            }else if (psn.superType === NODE_SUPERTYPE.SYSTEM && node.superType === NODE_SUPERTYPE.INFORMATION) {
                psnInsights.numInfoSupplied++
            }
        }
    })
    psnInsights.numActionsSupported = actionsSupported.length
    return actionsSupported
}

const setTotalCosts = (linqset:Linqset, edge:ProcessingEdge, nodes:LinqsetNode[], edges:LinqsetEdge[], ignoreFilter?:boolean) => {
    // add in this actions costs
    const node = edge.target

    const nodeInsights = node && getInsights(node, linqset)
    const previousInsights = edge.source && getInsights(edge.source, linqset)

    if (!nodeInsights) return

    if ((node.superType === NODE_SUPERTYPE.ACTION || node.superType === NODE_SUPERTYPE.OUTCOMES) && (ignoreFilter || !node.excluded)) {
        // initialise totals

        if (nodeInsights.totalHours === 0) {
            nodeInsights.totalHours = nodeInsights.hours
        }
        if (nodeInsights.totalCarbon === 0) {
            nodeInsights.totalCarbon = nodeInsights.allocatedCarbonByScope.total
        }
        if (nodeInsights.totalAllocatedCostsByType.total === 0) {
            addIndexedValues(nodeInsights.allocatedCostsByType, nodeInsights.totalAllocatedCostsByType)
        }
    }   

    // add current totals.
    // do not included the nodes from another model costs
    if (previousInsights && edge.edge && (linqset.isFilterModel() || edge.edge.type !== EDGE_TYPE.JOINS)) {
        addIndexedValues(previousInsights.totalAllocatedCostsByType, nodeInsights.totalAllocatedCostsByType, edge.edge.proportion)
        nodeInsights.totalCarbon += previousInsights.totalCarbon * edge.edge.proportion
        nodeInsights.totalHours += previousInsights.totalHours * edge.edge.proportion
    
        if (node.superType === NODE_SUPERTYPE.INFORMATION) {
            // we also need to set the numbers for information nodes based on the previous action node
            addIndexedValues(previousInsights.allocatedCostsByType, nodeInsights.allocatedCostsByType, edge.edge.proportion)
        }
    }

    const nodeEdges = edges.filter(e => e.type !== EDGE_TYPE.USED_IN && (e.sourceKey === node._key || e.targetKey === node._key))

    const processed = !previousInsights || !nodeEdges.find(e => {
        return (e.targetKey === node._key && !e.processed && (e.type === EDGE_TYPE.SUPPORT || e.type === EDGE_TYPE.CREATE))
    })
    const nextEdges = nodeEdges.filter(e => e.sourceKey === node._key && !e.processed)

    if (nextEdges.length === 0) {
        if (node.superType === NODE_SUPERTYPE.INFORMATION && (linqset.isFilterModel() || node.modelKey === linqset.modelKey)) nodeInsights.isOutput = true
        else if (previousInsights && edge.source && edge.source.superType === NODE_SUPERTYPE.INFORMATION && (linqset.isFilterModel() || edge.source.modelKey === linqset.modelKey)) previousInsights.isOutput = true
    }

    // only move forward if have completed processing all incoming
    if (processed) {
        nextEdges.map(e => {
            const en = nodes.find(n => n._key === e.targetKey)
            return new ProcessingEdge(node, e, en)
        }).forEach(e => {
            if (e.edge) e.edge.processed = true
            setTotalCosts(linqset, e, nodes, edges, ignoreFilter)
        })
    }
}

// fraction of a value (and of a revenue) that came from each other model; value flows proportionally so these stay
// constant as it is passed back through the model
type ValueShares = {value:{[modelKey:string]:number}, revenue:{[modelKey:string]:number}}

const createValueShares = (contributions:ModelValueContributions, value:number, revenue:number):ValueShares => {
    const shares:ValueShares = {value:{}, revenue:{}}
    Object.keys(contributions).forEach(k => {
        shares.value[k] = value > 0?contributions[k].value/value:0
        shares.revenue[k] = revenue > 0?contributions[k].revenue/revenue:0
    })
    return shares
}

// record how much of an amount of value/revenue added to a node came from each other model
const recordShares = (insights:NodeInsights, value:number, revenue:number, shares?:ValueShares) => {
    if (!shares) return
    const breakdown:ModelValueContributions = {}
    Object.keys(shares.value).forEach(k => breakdown[k] = {value:value*shares.value[k], revenue:revenue*(shares.revenue[k]||0)})
    if (!insights.propagatedFromModels) insights.propagatedFromModels = {}
    addValueContributions(breakdown, insights.propagatedFromModels)
}

/**
 * Passes an output's value back through the model from that node. Actions and information get the share of the value
 * that flows through them (a node's share is split over the nodes before it by previousNodeProportions); roles,
 * people, systems and infrastructure on the way are credited with the output's full value, once each.
 *
 * Each node passes value back once, after everything arriving at it from later nodes has been added up. Where paths
 * split and join again, the nodes before the split get the value from every path, not only the first one reached.
 * Nodes in a loop are passed through once, so a loop ends.
 *
 * visited collects the nodes passed through; credited the roles and systems already credited for this output.
 */
export const assignValues = (linqset:Linqset, node:LinqsetNode, insights:NodeInsights, value:number, revenue:number, nodes:LinqsetNode[], edges:LinqsetEdge[], visited:string[], fromOutcome:boolean=false, shares?:ValueShares, credited:string[]=[], root:{value:number, revenue:number}={value, revenue}) => {
    if (visited.indexOf(node._key) >= 0) return

    const nodeMap = new Map(nodes.filter(n => n.superType !== NODE_SUPERTYPE.NOTES).map(n => [n._key, n]))
    const incomingMap = new Map<string, LinqsetEdge[]>()
    edges.forEach(e => {
        const list = incomingMap.get(e.targetKey)
        if (list) list.push(e)
        else incomingMap.set(e.targetKey, [e])
    })

    type Arriving = {previousNode:LinqsetNode, e:LinqsetEdge}
    // the edges a node passes value back along: from action or information before it with a share of its items
    const chainOf = (n:LinqsetNode, nInsights:NodeInsights):Arriving[] => (incomingMap.get(n._key) || [])
        .map(e => ({e, previousNode: nodeMap.get(e.sourceKey) as LinqsetNode}))
        .filter(i => i.previousNode && isSupplyChain(i.previousNode.superType) && nInsights.previousNodeProportions?.map[i.e.sourceKey])

    // find the nodes value can reach, and for each how many of them it will receive value from
    const waitingFor = new Map<string, number>()
    const found:LinqsetNode[] = [node]
    const seen = new Set<string>([node._key])
    for (let i = 0; i < found.length; i++) {
        chainOf(found[i], getInsights(found[i], linqset)).forEach(({previousNode}) => {
            waitingFor.set(previousNode._key, (waitingFor.get(previousNode._key) || 0) + 1)
            if (!seen.has(previousNode._key)) {
                seen.add(previousNode._key)
                found.push(previousNode)
            }
        })
    }

    // the value and revenue each node has received, to pass back
    const received = new Map<string, {value:number, revenue:number}>([[node._key, {value, revenue}]])

    // each node this output's value reaches supports it once, however many routes reach it. Value passed back from an
    // outcome isn't counted again, as the outputs that realise it pass back their own
    const counted = new Set<string>()
    const supports = (target:LinqsetNode, targetInsights:NodeInsights) => {
        if (fromOutcome || counted.has(target._key)) return
        counted.add(target._key)
        targetInsights.outputsSupported++
    }
    supports(node, insights)

    // Roles, people, systems and infrastructure on any path back from an output are credited with that output's full
    // value (root), once per output however many of its steps they work on. Information and action nodes keep the
    // proportioned share of value flowing through them.
    const credit = (target:LinqsetNode, targetInsights:NodeInsights, v:number, r:number, direct:boolean) => {
        const once = !isSupplyChain(target.superType)
        if (once) {
            if (credited.indexOf(target._key) >= 0) return false
            credited.push(target._key)
            v = root.value
            r = root.revenue
        }
        supports(target, targetInsights)
        targetInsights.value += v
        targetInsights.revenue += r
        // value arriving at an information node straight from an outcome it realises is part of what that output originates
        if (direct && fromOutcome && target.superType === NODE_SUPERTYPE.INFORMATION) {
            targetInsights.originValue = (targetInsights.originValue||0) + v
            targetInsights.originRevenue = (targetInsights.originRevenue||0) + r
        }
        recordShares(targetInsights, v, r, shares)
        return true
    }

    const passBack = (n:LinqsetNode) => {
        visited.push(n._key)
        const nInsights = getInsights(n, linqset)
        const {value:v, revenue:r} = received.get(n._key) || {value:0, revenue:0}
        const direct = n === node
        const incoming = (incomingMap.get(n._key) || []).map(e => ({e, previousNode: nodeMap.get(e.sourceKey)})).filter(i => i.previousNode) as Arriving[]
        const chain = new Set(chainOf(n, nInsights).map(i => i.e))

        incoming.filter(i => !chain.has(i.e)).forEach(({e, previousNode}) => {
            // is role or system so check if any incoming roles or systems
            // we need to use visualisation key, cause only want node for this instance, not all connected nodes
            credit(previousNode, getInsights(previousNode, linqset), v, r, direct)

            if (!e.sourceVisualisationKey) return
            ;(incomingMap.get(previousNode._key) || []).filter(pe => pe.targetVisualisationKey === e.sourceVisualisationKey).forEach(pe => {
                const nextPreviousNode = nodeMap.get(pe.sourceKey)
                if (nextPreviousNode) {
                    const nextPreviousInsights = getInsights(nextPreviousNode, linqset)
                    credit(nextPreviousNode, nextPreviousInsights, v, r, false)
                }
            })
        })

        incoming.filter(i => chain.has(i.e)).forEach(({e, previousNode}) => {
            const proportion = nInsights.previousNodeProportions.map[e.sourceKey]
            const pv = v*proportion
            const pr = r*proportion
            credit(previousNode, getInsights(previousNode, linqset), pv, pr, direct)

            const total = received.get(previousNode._key) || {value:0, revenue:0}
            received.set(previousNode._key, {value: total.value + pv, revenue: total.revenue + pr})
            const waiting = (waitingFor.get(previousNode._key) || 0) - 1
            waitingFor.set(previousNode._key, waiting)
            if (waiting === 0 && visited.indexOf(previousNode._key) < 0) ready.push(previousNode)
        })
    }

    // pass value back from each node once everything arriving at it is in
    const ready:LinqsetNode[] = [node]
    while (ready.length > 0) passBack(ready.shift() as LinqsetNode)
    // nodes in a loop wait for each other; pass on what they have, once each
    found.filter(n => visited.indexOf(n._key) < 0).forEach(n => {
        if (visited.indexOf(n._key) < 0) {
            ready.push(n)
            while (ready.length > 0) {
                const next = ready.shift() as LinqsetNode
                if (visited.indexOf(next._key) < 0) passBack(next)
            }
        }
    })
}

const addToNodeGroupInsights = (list:NodeGroupModelInsights[], key:string, data:any) => {

    let existing = list.find(a => a.groupKey === key)
    if (!existing) {
        existing = createNodeGroupModelInsights(key)
        list.push(existing)
    }
    existing.hours += data.hours
    existing.numberOfStates ++
    if (existing.numberProcessed < data.numberProcessed) existing.numberProcessed = data.numberProcessed
    if (existing.value < data.value) existing.value = data.value
    
    addIndexedValues(data.allocatedCostsByType, existing.allocatedCostsByType)
    existing.allocatedCost = existing.allocatedCostsByType.total

    return existing
}

export const sumOrganisationModelInsightCosts = (orgInsights:ModelInsights, modelInsights:ModelInsights[]) => {
    const sums:any = {capacity:0, carbon:0, superTypes:{}}
    const modelKeys = modelInsights.map(i => i.modelKey)

    // initialise org model insights with totals from model insights.
    modelInsights.forEach(si => {
        Object.keys(si.superTypes).forEach(k => {
            if (!orgInsights.superTypes[k]) orgInsights.superTypes[k] = createInsightTotal()

            const sType = parseInt(k) as NODE_SUPERTYPE
            if (isModelNode(sType)) {
                // we add action and info costs and hours. Hours for role and systems were added into insights from the nodes. we can check them below
                orgInsights.superTypes[k].hours +=  si.superTypes[k].hours
                orgInsights.superTypes[k].allocatedCost +=  si.superTypes[k].allocatedCost
                orgInsights.superTypes[k].number +=  si.superTypes[k].number
            }else {
                // number checker, for roles and systems only, as these are set directly from nodes previously so worthwhile checking
                if (!sums.superTypes[k]) sums.superTypes[k] = {hours:0, cost:0}

                sums.superTypes[k].hours += si.superTypes[k].hours
                sums.superTypes[k].cost += si.superTypes[k].allocatedCost
            }

        })
        sums.capacity += si.modelledCapacity
        sums.carbon += si.allocatedCarbonByScope.total

        si.informationAssets.forEach(ia => {
            let total = orgInsights.informationAssets.find(t => t.groupKey === ia.groupKey)
            if (!total) {
                total = createNodeGroupModelInsights(ia.groupKey)
                orgInsights.informationAssets.push(total)
            }
            addAssetInsights(ia, total)
        })
        si.tags.forEach(ia => {
            addToNodeGroupInsights(orgInsights.tags, ia.groupKey, ia)
        })
        Object.keys(si.actionTypes).forEach(t => {
            if (!orgInsights.actionTypes[t]) orgInsights.actionTypes[t] = createActionType()
            orgInsights.actionTypes[t].processed += si.actionTypes[t].processed
            orgInsights.actionTypes[t].number += si.actionTypes[t].number
            orgInsights.actionTypes[t].hours += si.actionTypes[t].hours
            orgInsights.actionTypes[t].cost += si.actionTypes[t].cost
        })
        
        orgInsights.infoSuppliedBySystems += si.infoSuppliedBySystems

        // value an upstream model received from a downstream model is already counted in that downstream model
        const fromSet = getValueFromModelsInSet(si, modelKeys)
        orgInsights.outputValue += si.outputValue - fromSet.value
        orgInsights.outputRevenue += si.outputRevenue - fromSet.revenue
        orgInsights.numOutputs += si.numOutputs
        orgInsights.numOutputsProcessed += si.numOutputsProcessed
        orgInsights.numInputsExternal += si.numInputsExternal
        orgInsights.numInputsProcessed += si.numInputsExternal
        // the work that produced outputs with no value or revenue, model by model
        orgInsights.frictionCost += si.frictionCost
        orgInsights.frictionCarbon += si.frictionCarbon
        orgInsights.frictionTime += si.frictionTime
        orgInsights.exceptionCost += si.exceptionCost||0
        orgInsights.exceptionCarbon += si.exceptionCarbon||0
        orgInsights.exceptionTime += si.exceptionTime||0
        orgInsights.wasteCost = (orgInsights.wasteCost || 0) + (si.wasteCost || 0)
        orgInsights.wasteTime = (orgInsights.wasteTime || 0) + (si.wasteTime || 0)
        orgInsights.exceptionActions += si.exceptionActions||0

        roundModelInsights(si)
    })
    Object.keys(sums.superTypes).forEach(k => {
        const sType = parseInt(k) as NODE_SUPERTYPE
        const name = getSuperTypeName(sType)
        // we dont have hours on model infrastructure and people
        if (isRoleSystem(sType)) 
            checkEquality(sums.superTypes[sType].hours, orgInsights.superTypes[sType].hours, orgInsights.processingErrors, `Sum of model hours does not equal organisation hours for ${name}`)

        checkEquality(sums.superTypes[sType].cost, orgInsights.costAllocations.find(a => a.superType === sType)?.allocated||0, orgInsights.processingErrors, `Sum of model allocated cost does not equal organisation cost for ${name}`)            
    })

    const carbon = sumAllocations(orgInsights.emissions)
    checkEquality(sums.carbon, carbon.allocated, orgInsights.processingErrors, "Sum of model allocated carbon does not equal organisation allocated carbon")
    checkEquality(sums.capacity+orgInsights.unusedCapacity, orgInsights.modelledCapacity, orgInsights.processingErrors, "Sum of model capacity plus unused capacity does not equal organisation capacity")

    roundModelInsights(orgInsights)

}

export const totalNodeInsights = (linqset:Linqset, nodes:LinqsetNode[], ignoreFilter:boolean) => {
    const insights = createModelInsights(linqset.organisationKey, linqset.workspaceKey, linqset.modelKey)
    const realising = outputsRealisingOutcomes(linqset.edges)

    nodes.forEach(n => {
        const ni = getInsights(n, linqset)

        if (ignoreFilter || !n.excluded) {
            addNodeInsightsToModel(n, ni, insights, linqset.modelKey, realising)
        }
    })

    roundModelInsights(insights)

    return insights
}

export const createModelInsightsFromNodes = (linqset:Linqset, nodes:LinqsetNode[], ignoreFilter:boolean) => {
    const insights = createModelInsights(linqset.organisationKey, linqset.workspaceKey, linqset.modelKey)
    const realising = outputsRealisingOutcomes(linqset.edges)

    // used to check values below
    const indexes = [NODE_SUPERTYPE.PEOPLE, NODE_SUPERTYPE.ROLE, NODE_SUPERTYPE.SYSTEM, NODE_SUPERTYPE.INFRASTRUCTURE]
    const outputs = {hours:0, costs:initialiseIndexedValues(indexes), carbon:0}
    const inputs = {hours:0, costs:initialiseIndexedValues(indexes), carbon:0}

    // used to set capture node totals
    const startingNodeInsightsMap:any = {}
    const nodeInsights:NodeInsights[] = []

    nodes.forEach(n => {
        const ni = getInsights(n, linqset)

        if (ignoreFilter || !n.excluded) {
            addNodeInsightsToModel(n, ni, insights, linqset.modelKey, realising)
        }
        if (ni.isLastNode) {
            // last node for model so add up cumulative (total) numbers
            sumTotals(outputs, ni, indexes)
        }
        // we allocate stuff to starting nodes here based on proportions determined when calculating hours
        if (n.superType === NODE_SUPERTYPE.ACTION && ni.inputProportions) {
            Object.keys(ni.inputProportions.map).forEach((k:string) => {
                if (!startingNodeInsightsMap[k]) {
                    const startingNode = nodes.find(sn => sn._key === k)
                    const startingInsights = startingNode && getInsights(startingNode, linqset)
                    if (startingInsights) {
                        startingNodeInsightsMap[k] = startingInsights
                        startingInsights.totalHours = 0
                        startingInsights.totalCarbon = 0
                        startingInsights.totalAllocatedCostsByType = createIndexedValues()
                    }
                }
                if (startingNodeInsightsMap[k]) {
                    const proportion = ni.inputProportions.map[k] || 0 
                    startingNodeInsightsMap[k].totalHours += ni.hours * proportion
                    inputs.hours += ni.hours * proportion
                    startingNodeInsightsMap[k].totalCarbon += ni.allocatedCarbonByScope.total * proportion
                    inputs.carbon += ni.allocatedCarbonByScope.total * proportion
                    addIndexedValues(ni.allocatedCostsByType, startingNodeInsightsMap[k].totalAllocatedCostsByType, proportion)
                    addIndexedValues(ni.allocatedCostsByType, inputs.costs, proportion)
                }
            })
        }
        if (isRoleSystem(n.superType)) {
            // we need to total outputs supported into org insights at the end
            const orgInsights = getInsightsForNode(n, ORGANISATION_MODEL_KEY, linqset.workspaceKey)
            if (orgInsights) {
                orgInsights.outputsSupported = n.insights.filter(i => i.projectKey === linqset.workspaceKey && i.modelKey !== ORGANISATION_MODEL_KEY).reduce((t, i) => t += i.outputsSupported, 0)
            }
        }

        roundNodeInsights(ni)
        nodeInsights.push(ni)
    })

    // round the totals that were allocated above
    Object.keys(startingNodeInsightsMap).forEach((k:string) => roundNodeInsights(startingNodeInsightsMap[k]))

    insights.superTypes[NODE_SUPERTYPE.ACTION].percentageModelled=100
    insights.superTypes[NODE_SUPERTYPE.INFORMATION].percentageModelled=100

    const actionsDetails = Object.keys(insights.actionTypes).reduce((t, k) => {
        t.cost += insights.actionTypes[k].cost
        t.hours += insights.actionTypes[k].hours
        return t
    }, {cost:0, hours:0})

    const totalCost = getTotalModelCost(insights)

    checkEquality(actionsDetails.cost, totalCost, insights.processingErrors, "Total action type costs do not equal total costs")
    checkEquality(actionsDetails.hours, insights.superTypes[NODE_SUPERTYPE.ACTION].hours, insights.processingErrors, "Total action type hours do not equal total hours")

    if (!actionsDetails.hours) insights.processingErrors.push("There is no time allocated to the model. Set a duration for all actions and a frequency on model inputs. A tutorial on quantifying models is available via the help menu.")

    checkSums(inputs, insights, "input")
    checkSums(outputs, insights, "output")

    roundModelInsights(insights)

    return {insights, nodeInsights}
}

const checkSums = (sums:any, insights:ModelInsights, type:string) => {
    checkEquality(sums.hours, insights.superTypes[NODE_SUPERTYPE.ACTION].hours, insights.processingErrors, `Total of ${type} cumulative hours does not equal total action hours`)

    checkEquality(sums.costs.map[NODE_SUPERTYPE.PEOPLE], insights.superTypes[NODE_SUPERTYPE.PEOPLE].allocatedCost, insights.processingErrors, `Total of ${type} cumulative people costs does not equal total people cost`)
    checkEquality(sums.costs.map[NODE_SUPERTYPE.SYSTEM], insights.superTypes[NODE_SUPERTYPE.SYSTEM].allocatedCost, insights.processingErrors, `Total of ${type} cumulative system costs does not equal total system cost`)
    checkEquality(sums.costs.map[NODE_SUPERTYPE.INFRASTRUCTURE], insights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].allocatedCost, insights.processingErrors, `Total of ${type} cumulative infrastructure costs does not equal total infrastructure cost`)
    checkEquality(sums.costs.map[NODE_SUPERTYPE.ROLE], insights.superTypes[NODE_SUPERTYPE.ROLE].allocatedCost, insights.processingErrors, `Total of ${type} cumulative role costs does not equal total role cost`)

    checkEquality(sums.carbon, insights.allocatedCarbonByScope.total, insights.processingErrors, `Total of ${type} cumulative carbon does not equal total allocated carbon`)
}

const sumTotals = (sums:any, nodeInsights:NodeInsights, indexes:any[]) => {
    indexes.forEach(i => {
        sums.costs.map[i] += nodeInsights.totalAllocatedCostsByType.map[i] || 0
    })
    sums.carbon += nodeInsights.totalCarbon || 0
    sums.hours += nodeInsights.totalHours
}

const checkEquality = (a:number, b:number, errors:string[], msg:string) => {
    if (isNaN(a)) {
        errors.push(msg + ": Failed check because first parameter is NaN")
        return
    }

    if (isNaN(b)) {
        errors.push(msg + ": Failed check because second parameter is NaN")
        return
    }

    // amounts are rounded, so differences of a cent or less are not errors, and near zero a small difference would be
    // a large percentage of the first amount. Otherwise more than 3% of the larger amount is an error
    const diff = Math.abs(b-a)
    if (diff <= 0.01) return
    if (100*diff/Math.max(Math.abs(a), Math.abs(b)) > 3) errors.push(msg + ` (${a} versus ${b})`)
}

const setParentInsights = (linqset:Linqset, parentNode:LinqsetNode, actionInsights:NodeInsights, hours:number, e:LinqsetEdge) => {
    const insights = getInsights(parentNode, linqset)
    insights.hours += hours
    insights.numberProcessed += actionInsights.numberProcessed
    insights.numActionsSupported++

    const orgInsights = getInsightsForNode(parentNode, ORGANISATION_MODEL_KEY, linqset.workspaceKey)
    if (orgInsights) {
        // this actions hours as a proportion of overall organisation hours for this system/role
        const proportion = orgInsights.hours===0?1/orgInsights.numActionsSupported:hours/orgInsights.hours
        if (proportion > 0) {
            insights.proportion += proportion
            
            // add costs from parent node for this actions hours to parnet node and to action
            addIndexedValues(orgInsights.allocatedCostsByType, insights.allocatedCostsByType, proportion)

            if (isRoleSystem(parentNode.superType)) // only add to action insights once
                addIndexedValues(orgInsights.allocatedCostsByType, actionInsights.allocatedCostsByType, proportion)

            if (isSystemInfrastructure(parentNode.superType)) {
                addIndexedValues(orgInsights.allocatedCarbonByScope, insights.allocatedCarbonByScope, proportion)
                if (parentNode.superType === NODE_SUPERTYPE.SYSTEM)
                    addIndexedValues(orgInsights.allocatedCarbonByScope, actionInsights.allocatedCarbonByScope, proportion)
            }else if (isRolePeople(parentNode.superType)) {
                insights.numPeople = orgInsights.numPeople
                insights.modelledCapacity += orgInsights.modelledCapacity*proportion
            }
        }
        if (isRoleSystem(parentNode.superType)) {
            linqset.edges.filter(e2 => e2.targetKey === parentNode._key && (e2.type === EDGE_TYPE.FULFILS || e2.type === EDGE_TYPE.PROVIDES || (e2.type === EDGE_TYPE.USEBY && e2.targetVisualisationKey === e.sourceVisualisationKey))).forEach(e2 => {
                const nextParent = linqset.getNode(e2.sourceKey)
                if (nextParent && !nextParent.excluded) {
                    if (nextParent.superType === NODE_SUPERTYPE.PEOPLE) {

                        const hours = orgInsights.proportionOfPersonInRole?.map[nextParent._key]?(orgInsights.proportionOfPersonInRole.map[nextParent._key] * actionInsights.hours):0
                        if (hours) setParentInsights(linqset, nextParent, actionInsights, hours, e2)
                    }else setParentInsights(linqset, nextParent, actionInsights, actionInsights.hours, e2)
                }
            })
        }
    }
}

const createNodeInsightsForParents = (linqset:Linqset, actionNode:LinqsetNode) => {
    const actionInsights = getInsights(actionNode, linqset)

    linqset.edges.filter(e => e.targetKey === actionNode._key && (e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE)).forEach(e => {
        const parentNode = linqset.getNode(e.sourceKey)
        if (parentNode && !parentNode.excluded) {
            setParentInsights(linqset, parentNode, actionInsights, actionInsights.hours, e)
        }
    })
}

// Create linqsets with this rather than `new Linqset`: a filter (other than a model filter) also needs its insights
// generated here, which the Linqset constructor can't do as model processing builds on linqset-model
export const createLinqset = (organisation:Organisation, filter:InsightsFilter, graphData?:GraphData, organisationNodes?:LinqsetNode[]) => {
    const linqset = new Linqset(organisation, filter, graphData, organisationNodes)
    if (linqset.type !== LINQSET_TYPE.MODEL_FILTER && linqset.isFilterModel()) createFilteredInsightsForGraph(linqset)
    return linqset
}

// generated insights for a linqset created via some query
export const createFilteredInsightsForGraph = (linqset:Linqset) => {
    // make sure have no existing filtered insights
    linqset.nodes.forEach(n => {
        n.insights = n.insights.filter(i => !(i.modelKey === linqset.modelKey && i.projectKey === linqset.workspaceKey))
    })

    if (linqset.type === LINQSET_TYPE.CAPTURE || linqset.type === LINQSET_TYPE.OUTCOME) {
        const startingNodes = getStartingNodes(linqset.nodes, linqset.edges)

        // first set the number processed and hours on all action and info nodes
        setTimeForActionsAndInfo(linqset, startingNodes, false)

        // set time and num processed on org nodes and model
        linqset.nodes.filter(n => (n.superType === NODE_SUPERTYPE.ACTION && !n.excluded)).forEach(node => {
            // get insights for model node is in and then set this to be for this linqset
            createNodeInsightsForParents(linqset, node)
        })
        processModelCostsAndValue(linqset, startingNodes, false)
    }else {
        // set time and num processed on org nodes and model
        linqset.nodes.filter(n => (n.superType === NODE_SUPERTYPE.ACTION || n.superType === NODE_SUPERTYPE.INFORMATION) && !n.excluded).forEach(node => {
            // get insights for model node is in and then set this to be for this linqset
            const nodeInsights = getInsightsForNode(node, node.modelKey, linqset.workspaceKey)
            if (nodeInsights) {
                const filteredInsights = {...nodeInsights}
                filteredInsights.modelKey = linqset.modelKey
                resetNodeCostsForModel(filteredInsights)
                filteredInsights.value = nodeInsights.value
                filteredInsights.outputsSupported = nodeInsights.outputsSupported

                node.insights.push(filteredInsights)

                if (node.superType === NODE_SUPERTYPE.ACTION)
                    createNodeInsightsForParents(linqset, node)
            }
        })
    }
    // create model insights
    linqset.insights = createModelInsightsFromNodes(linqset, linqset.nodes, false).insights
    // update the insights in the model
    linqset.model.insights = linqset.model.insights.filter(i => i.projectKey !== linqset.workspaceKey)
    linqset.model.insights.push(linqset.insights)

    return linqset.insights
}